import { useEffect, useRef, useState } from "react";
import { api, ApiClientError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  interpolateFlowVariables,
  type ClarifyingQuestion,
  type GenerateAgentFlowResponse,
} from "@tw-pulse/shared";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";

/**
 * Full-screen, multi-step assisted flow creation:
 * Describe → (analyze) → Clarify → Generate (staged animation) → Preview → create.
 * Preview-only until the user confirms; nothing is persisted during generation.
 */
interface Props {
  onClose: () => void;
  onCreated: (flowId: string) => void;
}

type Step =
  | { name: "describe" }
  | { name: "analyzing" }
  | { name: "clarify" }
  | { name: "generating" }
  | { name: "preview"; result: GenerateAgentFlowResponse }
  | { name: "error"; message: string };

const STAGES = [
  "Understanding your business",
  "Designing intents",
  "Wiring actions",
  "Adding guardrails",
  "Final checks",
];
const STEP_LABELS = ["Describe", "Clarify", "Generate", "Review"];

function stepIndex(step: Step): number {
  switch (step.name) {
    case "describe":
      return 0;
    case "analyzing":
    case "clarify":
      return 1;
    case "generating":
    case "error":
      return 2;
    case "preview":
      return 3;
  }
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return reduced;
}

export function AssistedFlowBuilder({ onClose, onCreated }: Props) {
  const [step, setStep] = useState<Step>({ name: "describe" });
  const [description, setDescription] = useState("");
  const [businessContext, setBusinessContext] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [questions, setQuestions] = useState<ClarifyingQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [flowName, setFlowName] = useState("");
  const [creating, setCreating] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);

  const cancelledRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { me } = useAuth();

  // For the preview only — resolve {businessName} to what the user typed, or
  // the tenant's configured Branding name, so the draft reads naturally.
  const previewBusinessName = businessName.trim() || me?.tenant.businessName?.trim() || undefined;

  function hints() {
    return {
      businessName: businessName.trim() || undefined,
      industry: industry.trim() || undefined,
    };
  }

  // Combine the two context fields into the single description the API takes.
  function composedDescription() {
    const task = description.trim();
    const ctx = businessContext.trim();
    return ctx ? `${task}\n\nAbout the business: ${ctx}` : task;
  }

  async function analyze() {
    if (description.trim().length === 0) return;
    cancelledRef.current = false;
    setStep({ name: "analyzing" });
    try {
      const res = await api.agentflow.clarifyFlow({
        description: composedDescription(),
        ...hints(),
      });
      if (cancelledRef.current) return;
      if (res.questions.length > 0) {
        setQuestions(res.questions);
        setStep({ name: "clarify" });
      } else {
        void generate();
      }
    } catch {
      // Clarify is best-effort — proceed straight to generation on any failure.
      if (!cancelledRef.current) void generate();
    }
  }

  async function generate() {
    cancelledRef.current = false;
    setStep({ name: "generating" });
    const clarifications = questions
      .map((q) => ({ question: q.question, answer: (answers[q.id] ?? "").trim() }))
      .filter((c) => c.answer.length > 0);
    try {
      const result = await api.agentflow.generateFlow({
        description: composedDescription(),
        ...hints(),
        ...(clarifications.length > 0 ? { clarifications } : {}),
      });
      if (cancelledRef.current) return;
      setFlowName(businessName.trim() ? `${businessName.trim()} Agent` : "AI-generated flow");
      setStep({ name: "preview", result });
    } catch (e) {
      if (cancelledRef.current) return;
      setStep({
        name: "error",
        message: e instanceof ApiClientError ? e.message : "Failed to generate the flow.",
      });
    }
  }

  function cancelGenerating() {
    cancelledRef.current = true;
    setConfirmStop(false);
    setStep({ name: "describe" });
  }

  async function create(result: GenerateAgentFlowResponse) {
    const name = flowName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const flow = await api.agentflow.createFlow({ name, definition: result.definition });
      onCreated(flow.id);
    } catch (e) {
      setCreating(false);
      setStep({
        name: "error",
        message: e instanceof ApiClientError ? e.message : "Failed to create the flow.",
      });
    }
  }

  // Esc closes (confirm if a generation is in flight). Focus trap on Tab.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (step.name === "generating") {
          // When the confirm dialog is already open, its own Esc
          // handler closes it — opening it again here would fight that.
          if (!confirmStop) setConfirmStop(true);
        } else if (!creating) {
          onClose();
        }
        return;
      }
      if (e.key === "Tab" && containerRef.current) {
        const f = containerRef.current.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])',
        );
        const first = f[0];
        const last = f[f.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [step.name, creating, confirmStop, onClose]);

  const active = stepIndex(step);

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="assist-title"
      className="fixed inset-0 z-40 flex flex-col bg-zinc-50 dark:bg-zinc-950"
    >
      {/* Top bar */}
      <header className="flex items-center justify-between border-b border-zinc-200 px-5 py-3 dark:border-zinc-800">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
            AgentFlow
          </p>
          <h1
            id="assist-title"
            className="truncate text-[15px] font-semibold text-zinc-900 dark:text-zinc-100"
          >
            Build a flow with AI
          </h1>
        </div>
        <StepDots active={active} />
        <button
          type="button"
          onClick={() => {
            if (step.name === "generating") setConfirmStop(true);
            else if (!creating) onClose();
          }}
          disabled={creating}
          aria-label="Close"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
        >
          ✕
        </button>
      </header>

      {/* Center stage */}
      <main className="flex flex-1 items-start justify-center overflow-y-auto px-5 py-10">
        <div className="w-full max-w-2xl">
          {step.name === "describe" && (
            <DescribeStep
              description={description}
              setDescription={setDescription}
              businessContext={businessContext}
              setBusinessContext={setBusinessContext}
              businessName={businessName}
              setBusinessName={setBusinessName}
              industry={industry}
              setIndustry={setIndustry}
              onCancel={onClose}
              onContinue={() => void analyze()}
            />
          )}

          {step.name === "analyzing" && <AnalyzingStep onCancel={cancelGenerating} />}

          {step.name === "clarify" && (
            <ClarifyStep
              questions={questions}
              answers={answers}
              setAnswer={(id, v) => setAnswers((prev) => ({ ...prev, [id]: v }))}
              onBack={() => setStep({ name: "describe" })}
              onGenerate={() => void generate()}
            />
          )}

          {step.name === "generating" && <GeneratingStep onCancel={cancelGenerating} />}

          {step.name === "preview" && (
            <PreviewStep
              result={step.result}
              businessName={previewBusinessName}
              flowName={flowName}
              setFlowName={setFlowName}
              creating={creating}
              onBack={() => setStep({ name: "describe" })}
              onRegenerate={() => void generate()}
              onCreate={() => void create(step.result)}
            />
          )}

          {step.name === "error" && (
            <ErrorStep
              message={step.message}
              onBack={() => setStep({ name: "describe" })}
              onRetry={() => void generate()}
            />
          )}
        </div>
      </main>

      <ConfirmDialog
        open={confirmStop && step.name === "generating"}
        title="Stop generating?"
        description="Generation in progress will be discarded. Your description and answers are kept, so you can generate again."
        confirmLabel="Stop generating"
        cancelLabel="Keep going"
        onConfirm={cancelGenerating}
        onCancel={() => setConfirmStop(false)}
      />
    </div>
  );
}

function StepDots({ active }: { active: number }) {
  return (
    <ol className="hidden items-center gap-2 sm:flex" aria-label="Progress">
      {STEP_LABELS.map((label, i) => {
        const state = i < active ? "done" : i === active ? "current" : "todo";
        return (
          <li
            key={label}
            className="flex items-center gap-2"
            aria-current={state === "current" ? "step" : undefined}
          >
            <span
              className={
                "h-2 w-2 rounded-full transition-colors " +
                (state === "todo" ? "bg-zinc-300 dark:bg-zinc-700" : "bg-brand-500")
              }
            />
            <span
              className={
                "text-[12px] " +
                (state === "current"
                  ? "font-medium text-zinc-900 dark:text-zinc-100"
                  : "text-zinc-400 dark:text-zinc-500")
              }
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

const inputCls =
  "h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-[13.5px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 disabled:bg-zinc-50 disabled:text-zinc-400 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600";

function DescribeStep({
  description,
  setDescription,
  businessContext,
  setBusinessContext,
  businessName,
  setBusinessName,
  industry,
  setIndustry,
  onCancel,
  onContinue,
}: {
  description: string;
  setDescription: (v: string) => void;
  businessContext: string;
  setBusinessContext: (v: string) => void;
  businessName: string;
  setBusinessName: (v: string) => void;
  industry: string;
  setIndustry: (v: string) => void;
  onCancel: () => void;
  onContinue: () => void;
}) {
  const textareaCls =
    "w-full resize-y rounded-md border border-zinc-200 bg-white px-3 py-2.5 text-[13.5px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600";
  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Describe your agent
        </h2>
        <p className="mt-1 text-[13px] text-zinc-500">
          Two quick prompts — what your business is, and what the agent should do. We&apos;ll design
          the intents, actions, and guardrails for you to review before anything is saved.
        </p>
      </div>

      <div>
        <label
          htmlFor="assist-biz-ctx"
          className="mb-1.5 block text-[12px] font-medium text-zinc-600 dark:text-zinc-400"
        >
          About your business <span className="font-normal text-zinc-400">(optional)</span>
        </label>
        <textarea
          id="assist-biz-ctx"
          value={businessContext}
          onChange={(e) => setBusinessContext(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="e.g. We're a family-owned HVAC company serving the Dallas metro. We do AC repair, furnace tune-ups, and emergency leak service, weekdays 8–6."
          className={textareaCls}
        />
      </div>

      <div>
        <label
          htmlFor="assist-desc"
          className="mb-1.5 block text-[12px] font-medium text-zinc-600 dark:text-zinc-400"
        >
          What should the agent do?
        </label>
        <textarea
          id="assist-desc"
          autoFocus
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && description.trim()) {
              e.preventDefault();
              onContinue();
            }
          }}
          rows={5}
          maxLength={2000}
          placeholder="e.g. Capture the caller's name, address, and the problem, then book a next-day service visit. Answer questions about pricing and hours. Escalate gas-smell emergencies to a human immediately."
          className={textareaCls}
        />
        <p className="mt-1 text-right text-[11px] text-zinc-400">{description.length}/2000</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label
            htmlFor="assist-biz"
            className="mb-1.5 block text-[12px] font-medium text-zinc-600 dark:text-zinc-400"
          >
            Business name
          </label>
          <input
            id="assist-biz"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Acme HVAC"
            className={inputCls}
          />
        </div>
        <div>
          <label
            htmlFor="assist-ind"
            className="mb-1.5 block text-[12px] font-medium text-zinc-600 dark:text-zinc-400"
          >
            Industry
          </label>
          <input
            id="assist-ind"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder="Home services"
            className={inputCls}
          />
        </div>
      </div>

      <div className="flex items-center justify-between pt-1">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <div className="flex items-center gap-2">
          <span className="hidden text-[11px] text-zinc-400 sm:inline">⌘↵ to continue</span>
          <Button onClick={onContinue} disabled={description.trim().length === 0}>
            Continue
          </Button>
        </div>
      </div>
    </section>
  );
}

function AnalyzingStep({ onCancel }: { onCancel: () => void }) {
  return (
    <section className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <Spinner />
      <p className="text-[14px] font-medium text-zinc-700 dark:text-zinc-300">
        Reviewing your description…
      </p>
      <p className="text-[12.5px] text-zinc-500">
        Checking whether we need any quick clarifications.
      </p>
      <Button variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
    </section>
  );
}

// Answers are a comma-separated list so chips can be multi-selected AND the
// field stays freely typable. Chips toggle their own token in/out.
function answerTokens(answer: string): string[] {
  return answer
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}
function isChipSelected(answer: string, chip: string): boolean {
  return answerTokens(answer).some((t) => t.toLowerCase() === chip.toLowerCase());
}
function toggleChip(answer: string, chip: string): string {
  const tokens = answerTokens(answer);
  const idx = tokens.findIndex((t) => t.toLowerCase() === chip.toLowerCase());
  if (idx >= 0) tokens.splice(idx, 1);
  else tokens.push(chip);
  return tokens.join(", ");
}

function ClarifyStep({
  questions,
  answers,
  setAnswer,
  onBack,
  onGenerate,
}: {
  questions: ClarifyingQuestion[];
  answers: Record<string, string>;
  setAnswer: (id: string, v: string) => void;
  onBack: () => void;
  onGenerate: () => void;
}) {
  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          A few quick questions
        </h2>
        <p className="mt-1 text-[13px] text-zinc-500">
          Optional — pick any suggestions that apply or type your own. We&apos;ll fill in anything
          you skip.
        </p>
      </div>

      <ul className="space-y-4">
        {questions.map((q, i) => {
          const answer = answers[q.id] ?? "";
          return (
            <li
              key={q.id}
              className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900/40"
            >
              <label
                htmlFor={`q-${q.id}`}
                className="block text-[13.5px] font-medium text-zinc-900 dark:text-zinc-100"
              >
                {q.question}
              </label>
              <p className="mt-0.5 text-[12px] text-zinc-500">{q.why}</p>
              {q.suggestions.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {q.suggestions.map((s) => {
                    const selected = isChipSelected(answer, s);
                    return (
                      <button
                        key={s}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => setAnswer(q.id, toggleChip(answer, s))}
                        className={
                          "rounded-full border px-2.5 py-1 text-[12px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 " +
                          (selected
                            ? "border-brand-400 bg-brand-100 text-brand-700 dark:border-brand-500/50 dark:bg-brand-500/20 dark:text-brand-200"
                            : "border-zinc-200 text-zinc-600 hover:border-brand-300 hover:bg-brand-50/60 hover:text-brand-700 dark:border-zinc-700 dark:text-zinc-300 dark:hover:border-brand-500/40 dark:hover:bg-brand-500/10 dark:hover:text-brand-300")
                        }
                      >
                        {selected ? "✓ " : ""}
                        {s}
                      </button>
                    );
                  })}
                </div>
              )}
              <input
                id={`q-${q.id}`}
                autoFocus={i === 0}
                value={answer}
                onChange={(e) => setAnswer(q.id, e.target.value)}
                placeholder="Pick above or type your own…"
                className={"mt-2.5 " + inputCls}
              />
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-between pt-1">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onGenerate}>Generate flow</Button>
      </div>
    </section>
  );
}

function GeneratingStep({ onCancel }: { onCancel: () => void }) {
  const reduced = usePrefersReducedMotion();
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStage((p) => Math.min(p + 1, STAGES.length - 1)), 3500);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="flex flex-col items-center justify-center gap-6 py-14 text-center">
      <div className="relative flex h-16 w-16 items-center justify-center">
        <span
          className={
            "absolute inset-0 rounded-full bg-brand-500/15 " +
            (reduced ? "" : "motion-safe:animate-ping")
          }
        />
        <span className="relative h-10 w-10 rounded-full bg-brand-500/20" />
        <span className="absolute h-3 w-3 rounded-full bg-brand-500" />
      </div>

      <div className="min-h-[2.5rem]" aria-live="polite">
        <p className="text-[15px] font-medium text-zinc-800 dark:text-zinc-200">{STAGES[stage]}…</p>
        <p className="mt-1 text-[12.5px] text-zinc-500">This can take up to a minute.</p>
      </div>

      <ol className="flex items-center gap-1.5" aria-hidden="true">
        {STAGES.map((s, i) => (
          <li
            key={s}
            className={
              "h-1.5 w-8 rounded-full transition-colors " +
              (i <= stage ? "bg-brand-500" : "bg-zinc-200 dark:bg-zinc-800")
            }
          />
        ))}
      </ol>

      <Button variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
    </section>
  );
}

function PreviewStep({
  result,
  businessName,
  flowName,
  setFlowName,
  creating,
  onBack,
  onRegenerate,
  onCreate,
}: {
  result: GenerateAgentFlowResponse;
  businessName?: string;
  flowName: string;
  setFlowName: (v: string) => void;
  creating: boolean;
  onBack: () => void;
  onRegenerate: () => void;
  onCreate: () => void;
}) {
  const { definition, assumptions } = result;
  const values = businessName ? { businessName } : {};
  const greeting = interpolateFlowVariables(definition.greeting, values);
  const persona = interpolateFlowVariables(definition.persona, values);
  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Review your draft
        </h2>
        <p className="mt-1 text-[13px] text-zinc-500">
          Here&apos;s what we built. Create it to refine on the canvas — nothing is published yet.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 text-[11px] font-medium">
        {[
          [definition.stages.length, "stages"],
          [definition.slots.length, "slots"],
          [definition.actions.length, "actions"],
          [definition.guardrails.length, "guardrails"],
        ].map(([n, label]) => (
          <span
            key={label}
            className="rounded-full bg-brand-100 px-2.5 py-0.5 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
          >
            {n} {label}
          </span>
        ))}
      </div>

      <div className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900/40">
        <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Greeting</p>
        <p className="mt-0.5 text-[13.5px] italic text-zinc-700 dark:text-zinc-300">“{greeting}”</p>
        <p className="mt-3 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
          Persona
        </p>
        <p className="mt-0.5 text-[12.5px] text-zinc-600 dark:text-zinc-400">{persona}</p>
      </div>

      {assumptions.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
          <p className="text-[11px] font-medium uppercase tracking-wider text-amber-700 dark:text-amber-300">
            Review these — the AI filled gaps it wasn&apos;t sure about
          </p>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-[12.5px] text-amber-800 dark:text-amber-200/90">
            {assumptions.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <label
          htmlFor="assist-name"
          className="mb-1.5 block text-[12px] font-medium text-zinc-600 dark:text-zinc-400"
        >
          Flow name
        </label>
        <input
          id="assist-name"
          autoFocus
          value={flowName}
          onChange={(e) => setFlowName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && flowName.trim() && !creating) {
              e.preventDefault();
              onCreate();
            }
          }}
          placeholder="Name your flow…"
          className={inputCls}
        />
      </div>

      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-1">
          <Button variant="ghost" onClick={onBack} disabled={creating}>
            Back
          </Button>
          <Button variant="ghost" onClick={onRegenerate} disabled={creating}>
            Regenerate
          </Button>
        </div>
        <Button onClick={onCreate} disabled={creating || flowName.trim().length === 0}>
          {creating ? "Creating…" : "Create & open"}
        </Button>
      </div>
    </section>
  );
}

function ErrorStep({
  message,
  onBack,
  onRetry,
}: {
  message: string;
  onBack: () => void;
  onRetry: () => void;
}) {
  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          That didn&apos;t work
        </h2>
        <p
          role="alert"
          className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
        >
          {message}
        </p>
        <p className="mt-2 text-[12.5px] text-zinc-500">
          Try again, or go back and add more detail to your description.
        </p>
      </div>
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          Edit description
        </Button>
        <Button onClick={onRetry}>Try again</Button>
      </div>
    </section>
  );
}

function Spinner() {
  return (
    <span
      className="h-8 w-8 rounded-full border-2 border-zinc-300 border-t-brand-500 motion-safe:animate-spin dark:border-zinc-700 dark:border-t-brand-400"
      aria-hidden="true"
    />
  );
}
